import React, { useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { LobbyMapCards } from '../../CompanyManagement/components/LobbyMapCards'
import { CompanyService } from '../../../services/CompanyService'
import { trackEvent } from '../../../services/analyticsService'
import type { Company } from '../../../core/models/Company'
import { cleanDomainInput, emailDomainOf, isCommonEmailDomain } from '../../../../../types/EmailDomains'

/** What the old setup form switched on for every self-serve company; kept so nothing changes. */
const SELF_SERVE_FEATURES = {
  mapEditor: true,
  chat: true,
  whiteboard: true,
  videoCalling: true,
  screenSharing: true,
  fileSharing: true,
  customBranding: true,
  analytics: true,
  adminPanel: true,
}

const MAX_COMPANY_NAME_LENGTH = 100

/** Enough to look like a domain; whether it may be claimed is the cloud's call. */
const DOMAIN_SHAPE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/

/**
 * Create the company: its name, its domain and the map its lobby uses, on one screen (SC-438).
 * Confirming creates the company on that map; the person then moves on to the card step. The
 * domain works like the Domain field in company settings: it becomes the main domain and joins
 * the allowed domains, so people who sign up on it join the office. Branding and address are left
 * to company settings.
 *
 * The domain starts as the creator's own email domain. Someone on a personal email (gmail.com, …)
 * can't claim any domain, so they aren't asked.
 */
export default function CompanyStep({
  creatorEmail,
  onCreated,
}: {
  creatorEmail?: string | null
  onCreated: (company: Company) => void
}) {
  const creatorDomain = emailDomainOf(creatorEmail)
  const asksDomain = !!creatorDomain && !isCommonEmailDomain(creatorDomain)
  const [name, setName] = useState('')
  const [domain, setDomain] = useState(asksDomain ? creatorDomain : '')
  const [domainInvalid, setDomainInvalid] = useState(false)
  const typedDomain = cleanDomainInput(domain)
  const [lobbyMapId, setLobbyMapId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [nameMissing, setNameMissing] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setNameMissing(true)
      return
    }
    const cleanDomain = asksDomain ? cleanDomainInput(domain) : ''
    if (cleanDomain && !DOMAIN_SHAPE.test(cleanDomain)) {
      setDomainInvalid(true)
      return
    }
    if (!lobbyMapId) {
      setError('Pick a layout for your office.')
      return
    }
    setBusy(true)
    setError('')
    const slug = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    const result = await CompanyService.selfServeCreateCompany({
      name: trimmed,
      lobbyMapId,
      ...(cleanDomain ? { domain: cleanDomain } : {}),
      platform_name: trimmed,
      platform_url: `https://${slug || 'office'}.sevv.com`,
      features: SELF_SERVE_FEATURES,
    } as Parameters<typeof CompanyService.selfServeCreateCompany>[0])
    if (result.success && result.company) {
      trackEvent(
        'APP_company_creation_success',
        { source: 'self_serve', company_id: result.company.id ?? null, company_name: result.company.name ?? null },
        'Company'
      )
      onCreated(result.company)
      return
    }
    setError(result.error || 'Your office could not be created. Try again.')
    setBusy(false)
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate data-testid="onboarding-company-step">
      <Typography variant="h5" component="h1" fontWeight={700}>
        Set up your company office
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Name your office and pick the floor your team will walk around on. You can invite the team next.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          id="onboarding-company-name"
          label="Company / office name"
          placeholder="e.g. Acme Studio"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            setNameMissing(false)
          }}
          error={nameMissing}
          helperText={nameMissing ? 'Give the office a name.' : undefined}
          inputProps={{ maxLength: MAX_COMPANY_NAME_LENGTH }}
          disabled={busy}
          required
          fullWidth
        />

        {asksDomain && (
          <TextField
            id="onboarding-company-domain"
            label="Company domain"
            placeholder={creatorDomain}
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value)
              setDomainInvalid(false)
            }}
            error={domainInvalid}
            helperText={
              domainInvalid
                ? 'Enter a domain like acme.com.'
                : DOMAIN_SHAPE.test(typedDomain)
                  ? `Anyone who signs up with an @${typedDomain} email joins your office automatically, no invite needed. You can change this later in company settings.`
                  : 'Add your company’s domain so teammates can join without an invite.'
            }
            disabled={busy}
            fullWidth
          />
        )}

        <Box>
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Pick your office layout
          </Typography>
          <LobbyMapCards value={lobbyMapId} onChange={setLobbyMapId} disabled={busy} />
        </Box>

        <Button type="submit" variant="contained" size="large" disabled={busy}>
          {busy ? 'Creating your office…' : 'Create office'}
        </Button>
      </Stack>
    </Box>
  )
}
